import { Box, Button, Typography } from "@mui/material";
import CheckboxDropdown from "./CheckboxDropdown";
import RangeSliderDropdown from "./RangeSliderDropdown";
import InputFieldDropdown from "./InputFieldDropdown";
import RadioButtonDropdown from "./RadioButtonDropdown";
import React from "react";
import { useCumulativeCounts } from "../../utils/useCumulativeCounts";
import PublicIcon from "@mui/icons-material/Public";
import CloseIcon from "@mui/icons-material/Close";

interface FiltersWidgetProps {
  prices: number[];
  priceMin?: number;
  priceMax?: number;
  yearMin?: number;
  yearMax?: number;
  lengthMin?: number;
  lengthMax?: number;
  years: any[];
  lengths: number[];
  boatsFilter: string[];
  setBoatsFilter: React.Dispatch<React.SetStateAction<string[]>>;
  cabinsFilter: string[];
  setCabinsFilter: React.Dispatch<React.SetStateAction<string[]>>;
  bathroomFilters: string[];
  setBathroomFilter: React.Dispatch<React.SetStateAction<string[]>>;
  priceFilter: number[];
  setPriceFilter: (value: number[]) => void;
  priceUserSet?: boolean;
  yearFilter: number[];
  setyearFilter: (value: number[]) => void;
  yearUserSet?: boolean;
  lengthFilter: number[];
  setLengthFilter: (value: number[]) => void;
  lengthUserSet?: boolean;
  amenitiesFilter: string[];
  setAmenitiesFilter: React.Dispatch<React.SetStateAction<string[]>>;
  orderFilter: string;
  setOrderFilter: React.Dispatch<React.SetStateAction<string>>;
  buildersInput: string[];
  setBuildersInput: React.Dispatch<React.SetStateAction<string[]>>;
  clusterYachts: any[];
  setClusterYachts: React.Dispatch<React.SetStateAction<any[]>>;
  yachtModels: string[];
  filterCount?: number;
  onClearFilters?: () => void;
}

const FiltersWidget = ({
  prices,
  priceMin = 552,
  priceMax = 61658,
  yearMin = 1990,
  yearMax = 2025,
  lengthMin = 20,
  lengthMax = 120,
  years,
  lengths,
  boatsFilter,
  setBoatsFilter,
  cabinsFilter,
  setCabinsFilter,
  bathroomFilters,
  setBathroomFilter,
  priceFilter,
  setPriceFilter,
  priceUserSet,
  yearFilter,
  setyearFilter,
  yearUserSet,
  lengthFilter,
  setLengthFilter,
  lengthUserSet,
  amenitiesFilter,
  setAmenitiesFilter,
  orderFilter,
  setOrderFilter,
  buildersInput,
  setBuildersInput,
  setClusterYachts,
  clusterYachts,
  yachtModels,
  filterCount = 0,
  onClearFilters,
}: FiltersWidgetProps) => {
  const { counts, amenities } = useCumulativeCounts();
  return (
    <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1, flexWrap: 'wrap' }}>
      <CheckboxDropdown
        title="Boat Type"
        selectedFilters={boatsFilter}
        setSelectedFilters={setBoatsFilter}
        options={(counts?.yacht_types ?? []).map(({ name, count }) => ({
          label: name,
          count,
        }))}
      />

      <CheckboxDropdown
        title="Cabins"
        selectedFilters={cabinsFilter}
        setSelectedFilters={setCabinsFilter}
        options={(counts?.cabins ?? []).map(({ name, count }) => ({
          label: name,
          count,
        }))}
      />
      <CheckboxDropdown
        title="Bathrooms"
        selectedFilters={bathroomFilters}
        setSelectedFilters={setBathroomFilter}
        options={(counts?.bathrooms ?? []).map(({ name, count }) => ({
          label: name,
          count,
        }))}
      />

      <RangeSliderDropdown
        title="Price"
        min={priceMin}
        max={priceMax}
        range={priceFilter}
        setRange={setPriceFilter}
        histogramValues={prices}
        userSet={priceUserSet}
      />

      <RangeSliderDropdown
        title="Year"
        min={yearMin}
        max={yearMax}
        range={yearFilter}
        setRange={setyearFilter}
        histogramValues={years}
        userSet={yearUserSet}
      />

      <RangeSliderDropdown
        title="Length"
        min={lengthMin}
        max={lengthMax}
        range={lengthFilter}
        setRange={setLengthFilter}
        histogramValues={lengths}
        userSet={lengthUserSet}
      />

      <InputFieldDropdown
        title="Boat Model"
        selectedFilter={buildersInput}
        setSelectedFilter={setBuildersInput}
        inputValue={buildersInput}
        setInputValue={setBuildersInput}
        options={yachtModels}
      />

      <CheckboxDropdown
        title="Equipment"
        selectedFilters={amenitiesFilter}
        setSelectedFilters={setAmenitiesFilter}
        options={[
          { label: "Furling Mainsail" },
          ...[...amenities].filter(a => a.label.toLowerCase() !== 'furling mainsail' && !a.label.toLowerCase().includes('unknown')),
        ].sort((a, b) => a.label.localeCompare(b.label))}
      />

      <RadioButtonDropdown
        title="Order by"
        selectedOption={orderFilter}
        setSelectedOption={setOrderFilter}
        options={[
          // "Recommended",
          "Lowest price",
          "Lowest cabins",
          "Highest discount",
          "Increasing Length",
        ]}
      />
      {clusterYachts?.length > 0 && (
        <Button
          variant="outlined"
          startIcon={<PublicIcon />}
          endIcon={<CloseIcon />}
          sx={{
            borderRadius: "9999px",
            textTransform: "none",
            backgroundColor: "#f5f5f5",
            color: "#333",
            borderColor: "#ccc",
            paddingX: 2,
            paddingY: 0.5,
            fontSize: "0.875rem",
            "&:hover": { backgroundColor: "#e0e0e0", borderColor: "#bbb" },
          }}
          onClick={() => setClusterYachts([])}
        >
          Map
        </Button>
      )}
      {filterCount > 0 && onClearFilters && (
        <Typography
          variant="body2"
          onClick={onClearFilters}
          sx={{
            alignSelf: 'center',
            cursor: 'pointer',
            color: '#1a1a2e',
            textDecoration: 'underline',
            fontWeight: 500,
            whiteSpace: 'nowrap',
          }}
        >
          Remove Filters
        </Typography>
      )}
    </Box>
  );
};

export default FiltersWidget;
